import type { CreatePriceRequest } from '@domains/subscription/priv/Billables/api/api'
import {
  type AssignedPriceMapper,
  mapAssignedPaymentFrequency,
  mapPaymentFrequencyToPriceFrequency,
  type PriceRequestMapper,
} from '@domains/subscription/priv/Billables/api/mappers'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type {
  AssignedPriceAlgorithm,
  PriceRequest,
  TemplateAssignedPrice,
  TemplatePriceRequest,
  TieredAlgorithm,
  TierRange,
} from '@mynt/backoffice-api-spec/billables'
import type { TieredPrice, TieredPriceRequest } from './tiered'

export const isTiered = <T extends BaseAlgorithm>(billable: unknown): billable is TieredPrice<T> => {
  return (billable as TieredPrice<T>)?.type === 'Tiered'
}

export const isTieredAssignedPrice = (price: TemplateAssignedPrice): price is TieredAssignedPrice => {
  return price.algorithm.type === 'Tiered'
}

export class TieredPriceRequestMapper<T extends CreatePriceRequest> implements PriceRequestMapper {
  supports(price: CreatePriceRequest): price is TieredPriceRequest<T> {
    return price.type === 'Tiered'
  }

  toAssignedPriceAlgorithm(price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper): AssignedPriceAlgorithm {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in TieredPriceRequestMapper:toAssignedPriceAlgorithm: ${price.type}`)
    }

    return {
      type: 'Tiered',
      tiers: price.tiers.map((tier) => {
        if (!this.supports(tier.algorithm)) {
          throw new Error(`Unsupported tier algorithm type in TieredPriceRequestMapper:toAssignedPriceAlgorithm: ${tier.algorithm.type}`)
        }

        return {
          range: tier.range as TierRange,
          algorithm: mapper(tier.algorithm).toAssignedPriceAlgorithm(tier.algorithm, mapper),
        }
      }),
    }
  }

  toPriceRequestDto(price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper): TemplatePriceRequest {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in TieredPriceRequestMapper:toPriceRequestDto: ${price.type}`)
    }

    return {
      algorithm: {
        type: 'Tiered',
        tiers: price.tiers.map((tier) => {
          if (!mapper(tier.algorithm).supports(tier.algorithm)) {
            throw new Error(`Unsupported tier algorithm type in TieredPriceRequestMapper:toPriceRequestDto: ${tier.algorithm.type}`)
          }

          return {
            range: tier.range as TierRange,
            algorithm: mapper(tier.algorithm).toAssignedPriceAlgorithm(tier.algorithm, mapper),
          }
        }),
      },
      priceFrequency: mapPaymentFrequencyToPriceFrequency(price.paymentFrequency),
    }
  }
}

type TieredAssignedPrice = TemplateAssignedPrice & {
  algorithm: TieredAlgorithm
}

export class TieredAssignedPriceMapper implements AssignedPriceMapper {
  supports(assignedPrice: TemplateAssignedPrice) {
    return isTieredAssignedPrice(assignedPrice)
  }

  toBillablePrice(assignedPrice: TemplateAssignedPrice): TieredPrice<BaseAlgorithm> {
    if (!isTieredAssignedPrice(assignedPrice)) {
      throw new Error(`Unsupported assigned price type in TieredAssignedPriceMapper: ${assignedPrice.algorithm.type}`)
    }

    return {
      billableId: assignedPrice.billableId,
      type: 'Tiered',
      tiers: assignedPrice.algorithm.tiers.map((tier) => ({
        ...tier,
        range: { fromExclusive: tier.range.fromExclusive ?? 0, toInclusive: tier.range.toInclusive },
      })),
      paymentFrequency: mapAssignedPaymentFrequency(assignedPrice.priceFrequency),
      supported: true,
    }
  }
}
